/**
 * Content Gaps Page
 * 
 * Displays topic opportunities identified as missing or underrepresented
 * in the user's content. Shows gaps grouped by category with top posts.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Loader2, 
  Target,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  Star,
  ExternalLink,
  Sparkles,
  BookOpen,
  FileText,
  TriangleAlert,
  Square,
  ClipboardList
} from 'lucide-react';
import { useSite } from '../../hooks/useSite';
import { analysisApi } from '../../lib/dashboard-api';

// ============================================
// Types (simplified, normalized)
// ============================================

interface NormalizedContentGap {
  topic: string;
  category: string;
  priority: 'high' | 'medium' | 'low';
}

interface NormalizedTopPost {
  postId: string;
  postTitle: string;
  postUrl: string | null;
  ranking: number;
  compositeScore: number;
}

interface StepByStepItem {
  action: string;
  details: string;
  exampleFromPosts: string;
}

interface WritingTemplate {
  overview: string;
  keySections: string[];
  targetAudience: string;
  estimatedLength: string;
  recommendedFormat: string;
  suggestedStructure: string[];
  length: string;
  tone: string;
}

interface ContentGuidance {
  whatMakesTheseWork: string;
  keySuccessPatterns: string[];
  howToReplicate: {
    overview: string;
    stepByStep: StepByStepItem[];
    quickChecklist: string[];
  } | null;
  writingTemplate: WritingTemplate | null;
  whatToAvoid: string[];
}

interface NormalizedHighValueAnalysis {
  category: string;
  topPosts: NormalizedTopPost[];
  contentGuidance: ContentGuidance | null;
}

interface NormalizedData {
  hasResults: boolean;
  analysisDate: string | null;
  totalGaps: number;
  gaps: NormalizedContentGap[];
  gapsByCategory: Map<string, NormalizedContentGap[]>;
  highValueByCategory: Map<string, NormalizedHighValueAnalysis>;
}

// ============================================
// Data Normalization (done BEFORE React state)
// ============================================

/**
 * Safely extract a string value from any input
 */
function safeString(value: unknown, fallback: string = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return fallback;
}

/**
 * Safely extract a number value from any input
 */
function safeNumber(value: unknown, fallback: number = 0): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }
  return fallback;
}

/**
 * Safely extract a string array from any input
 */
function safeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string | number => typeof item === 'string' || typeof item === 'number')
    .map(item => String(item));
}

/**
 * Normalize raw API response into safe, renderable data
 * This ensures NO objects can leak through to React rendering
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeApiResponse(response: any): NormalizedData {
  // Default empty state
  if (!response || !response.hasResults) {
    return {
      hasResults: false,
      analysisDate: null,
      totalGaps: 0,
      gaps: [],
      gapsByCategory: new Map(),
      highValueByCategory: new Map(),
    };
  }

  // Normalize content gaps - extract ONLY primitive values
  const rawGaps = response.contentGaps || [];
  const gaps: NormalizedContentGap[] = [];
  
  for (const rawGap of rawGaps) {
    if (!rawGap || typeof rawGap !== 'object') continue;
    
    gaps.push({
      topic: safeString(rawGap.topic, 'Untitled Topic'),
      category: safeString(rawGap.category, 'Uncategorized'),
      priority: ['high', 'medium', 'low'].includes(rawGap.priority) 
        ? rawGap.priority 
        : 'medium',
    });
  }

  // Group gaps by category
  const gapsByCategory = new Map<string, NormalizedContentGap[]>();
  for (const gap of gaps) {
    const existing = gapsByCategory.get(gap.category) || [];
    existing.push(gap);
    gapsByCategory.set(gap.category, existing);
  }

  // Normalize high-value analyses - extract top posts AND content guidance
  const rawHighValue = response.highValueAnalyses || [];
  const highValueByCategory = new Map<string, NormalizedHighValueAnalysis>();
  
  for (const hva of rawHighValue) {
    if (!hva || typeof hva !== 'object') continue;
    
    const category = safeString(hva.category);
    if (!category) continue;
    
    // Extract top posts - ONLY primitive values
    const rawPosts = hva.topValuablePosts || hva.top_valuable_posts || [];
    const topPosts: NormalizedTopPost[] = [];
    
    for (const post of rawPosts) {
      if (!post || typeof post !== 'object') continue;
      const postId = safeString(post.postId || post.post_id);
      topPosts.push({
        postId,
        postTitle: safeString(post.postTitle || post.post_title || post.title, `Post #${postId}`),
        postUrl: safeString(post.postUrl || post.post_url) || null,
        ranking: safeNumber(post.ranking),
        compositeScore: safeNumber(post.compositeScore || post.composite_score),
      });
    }
    
    // Only keep top 3
    topPosts.sort((a, b) => a.ranking - b.ranking);
    
    // Extract content guidance - safely convert all nested objects to primitives
    const rawGuidance = hva.contentGuidance || hva.content_guidance;
    let contentGuidance: ContentGuidance | null = null;
    
    if (rawGuidance && typeof rawGuidance === 'object') {
      // Extract howToReplicate
      const rawHowTo = rawGuidance.howToReplicate || rawGuidance.how_to_replicate;
      let howToReplicate: ContentGuidance['howToReplicate'] = null;
      
      if (rawHowTo && typeof rawHowTo === 'object') {
        const rawSteps = rawHowTo.stepByStep || rawHowTo.step_by_step || [];
        howToReplicate = {
          overview: safeString(rawHowTo.overview),
          stepByStep: Array.isArray(rawSteps) ? rawSteps.map((step: unknown) => {
            const s = step as Record<string, unknown>;
            return {
              action: safeString(s?.action),
              details: safeString(s?.details),
              exampleFromPosts: safeString(s?.exampleFromPosts || s?.example_from_posts),
            };
          }) : [],
          quickChecklist: safeStringArray(rawHowTo.quickChecklist || rawHowTo.quick_checklist),
        };
      }
      
      // Extract writingTemplate
      const rawTemplate = rawGuidance.writingTemplate || rawGuidance.writing_template;
      let writingTemplate: WritingTemplate | null = null;
      
      if (rawTemplate && typeof rawTemplate === 'object') {
        writingTemplate = {
          overview: safeString(rawTemplate.overview),
          keySections: safeStringArray(rawTemplate.keySections || rawTemplate.key_sections),
          targetAudience: safeString(rawTemplate.targetAudience || rawTemplate.target_audience),
          estimatedLength: safeString(rawTemplate.estimatedLength || rawTemplate.estimated_length),
          recommendedFormat: safeString(rawTemplate.recommendedFormat || rawTemplate.recommended_format),
          suggestedStructure: safeStringArray(rawTemplate.suggestedStructure || rawTemplate.suggested_structure),
          length: safeString(rawTemplate.length),
          tone: safeString(rawTemplate.tone),
        };
      }
      
      contentGuidance = {
        whatMakesTheseWork: safeString(rawGuidance.whatMakesTheseWork || rawGuidance.what_makes_these_work),
        keySuccessPatterns: safeStringArray(rawGuidance.keySuccessPatterns || rawGuidance.key_success_patterns),
        howToReplicate,
        writingTemplate,
        whatToAvoid: safeStringArray(rawGuidance.whatToAvoid || rawGuidance.what_to_avoid),
      };
    }
    
    highValueByCategory.set(category, {
      category,
      topPosts: topPosts.slice(0, 3),
      contentGuidance,
    });
  }

  return {
    hasResults: true,
    analysisDate: safeString(response.analysisDate) || null,
    totalGaps: gaps.length,
    gaps,
    gapsByCategory,
    highValueByCategory,
  };
}

// ============================================
// Constants
// ============================================

const PRIORITY_BADGE_STYLES: Record<string, string> = {
  high: 'bg-red-500 text-white',
  medium: 'bg-orange-500 text-white',
  low: 'bg-gray-500 text-white',
};

const RANK_EMOJI: Record<number, string> = {
  1: '🥇',
  2: '🥈',
  3: '🥉',
};

// ============================================
// Sub-Components
// ============================================

function PriorityBadge({ priority }: { priority: string }) {
  const style = PRIORITY_BADGE_STYLES[priority] || PRIORITY_BADGE_STYLES.medium;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${style}`}>
      {priority}
    </span>
  );
}

function TopPostsList({ posts }: { posts: NormalizedTopPost[] }) {
  if (posts.length === 0) return null;
  
  return (
    <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
      <div className="flex items-center gap-2 mb-3">
        <Star className="w-4 h-4 text-[var(--vizzex-yellow)]" />
        <span className="text-sm font-medium text-white/70">Top Performing Posts in this Category</span>
      </div>
      <div className="space-y-2">
        {posts.map((post) => (
          <div 
            key={post.postId}
            className="flex items-center gap-3 p-2 bg-[var(--bg-tertiary)] rounded"
          >
            <span className="text-lg">{RANK_EMOJI[post.ranking] || `#${post.ranking}`}</span>
            <div className="flex-1 min-w-0">
              <span className="text-white text-sm">
                {post.postTitle}
                <span className="text-white/50 ml-1">({post.postId})</span>
              </span>
            </div>
            <span className="text-[var(--vizzex-yellow)] text-sm font-bold">
              Score: {post.compositeScore.toFixed(1)}
            </span>
            {post.postUrl && (
              <a
                href={post.postUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--vizzex-yellow)] hover:underline text-sm flex items-center gap-1"
              >
                View <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Content Guidance Section - displays PRO-level content guidance
 */
function ContentGuidanceSection({ guidance }: { guidance: ContentGuidance }) {
  const [expanded, setExpanded] = useState(false);
  
  // Check if there's any guidance content to show
  const hasContent = guidance.whatMakesTheseWork || 
    guidance.keySuccessPatterns.length > 0 || 
    guidance.howToReplicate || 
    guidance.writingTemplate || 
    guidance.whatToAvoid.length > 0;
  
  if (!hasContent) return null;
  
  return (
    <div className="mt-4 pt-4 border-t border-[var(--border-color)]">
      {/* Toggle Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-[var(--vizzex-yellow)] hover:underline"
      >
        <BookOpen className="w-4 h-4" />
        <span className="font-medium">View Content Analysis & Guidance</span>
        {expanded ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>
      
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* What Makes These Work */}
          {guidance.whatMakesTheseWork && (
            <div className="bg-white/5 rounded-lg p-4 border-l-4 border-[var(--vizzex-yellow)]">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-4 h-4 text-[var(--vizzex-yellow)]" />
                <span className="font-semibold text-[var(--vizzex-yellow)]">What Makes These Work</span>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">{guidance.whatMakesTheseWork}</p>
            </div>
          )}
          
          {/* Key Success Patterns */}
          {guidance.keySuccessPatterns.length > 0 && (
            <div className="bg-white/5 rounded-lg p-4 border-l-4 border-[var(--vizzex-yellow)]">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[var(--vizzex-yellow)]" />
                <span className="font-semibold text-[var(--vizzex-yellow)]">Key Success Patterns</span>
              </div>
              <ul className="space-y-2">
                {guidance.keySuccessPatterns.map((pattern, idx) => (
                  <li key={idx} className="text-white/80 text-sm leading-relaxed pl-4 border-l-2 border-white/20">
                    {pattern}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {/* How to Replicate This Success */}
          {guidance.howToReplicate && (
            <div className="bg-[#e8f5e9] rounded-lg p-4 border-l-4 border-[#4caf50]">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardList className="w-4 h-4 text-[#2e7d32]" />
                <span className="font-semibold text-[#2e7d32]">How to Replicate This Success</span>
              </div>
              {guidance.howToReplicate.overview && (
                <p className="text-gray-700 text-sm italic mb-3 leading-relaxed">
                  {guidance.howToReplicate.overview}
                </p>
              )}
              
              {/* Step by Step */}
              {guidance.howToReplicate.stepByStep.length > 0 && (
                <div className="mb-3">
                  <span className="text-gray-600 text-sm font-medium">Step-by-Step:</span>
                  <ol className="list-decimal list-inside mt-2 space-y-3">
                    {guidance.howToReplicate.stepByStep.map((step, idx) => (
                      <li key={idx} className="text-gray-700 text-sm">
                        <span className="font-semibold">{step.action}</span>
                        {step.details && (
                          <p className="ml-5 text-gray-600 mt-1">{step.details}</p>
                        )}
                        {step.exampleFromPosts && (
                          <p className="ml-5 text-gray-500 italic mt-1">
                            Example: {step.exampleFromPosts}
                          </p>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              
              {/* Quick Checklist */}
              {guidance.howToReplicate.quickChecklist.length > 0 && (
                <div className="bg-white/50 rounded p-3 mt-3">
                  <span className="text-gray-600 text-sm font-medium">Quick Checklist:</span>
                  <ul className="mt-2 space-y-1">
                    {guidance.howToReplicate.quickChecklist.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700 text-sm">
                        <Square className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Writing Template */}
          {guidance.writingTemplate && (
            <div className="bg-[#fff3e0] rounded-lg p-4 border-l-4 border-[#ff9800]">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-[#e65100]" />
                <span className="font-semibold text-[#e65100]">Writing Template</span>
              </div>
              
              {guidance.writingTemplate.suggestedStructure.length > 0 && (
                <div className="mb-3">
                  <span className="text-gray-600 text-sm font-medium">Suggested Structure:</span>
                  <ol className="list-decimal list-inside mt-2 space-y-1">
                    {guidance.writingTemplate.suggestedStructure.map((item, idx) => (
                      <li key={idx} className="text-gray-700 text-sm">{item}</li>
                    ))}
                  </ol>
                </div>
              )}
              
              {guidance.writingTemplate.length && (
                <p className="text-gray-700 text-sm mb-2">
                  <span className="mr-1">✏️</span>
                  <span className="font-medium">Length:</span> {guidance.writingTemplate.length}
                </p>
              )}
              
              {guidance.writingTemplate.tone && (
                <p className="text-gray-700 text-sm">
                  <span className="mr-1">🎭</span>
                  <span className="font-medium">Tone:</span> {guidance.writingTemplate.tone}
                </p>
              )}
            </div>
          )}
          
          {/* What to Avoid */}
          {guidance.whatToAvoid.length > 0 && (
            <div className="bg-[#ffebee] rounded-lg p-4 border-l-4 border-[#f44336]">
              <div className="flex items-center gap-2 mb-3">
                <TriangleAlert className="w-4 h-4 text-[#c62828]" />
                <span className="font-semibold text-[#c62828]">What to Avoid</span>
              </div>
              <ul className="space-y-2">
                {guidance.whatToAvoid.map((item, idx) => (
                  <li key={idx} className="text-gray-700 text-sm leading-relaxed pl-4 border-l-2 border-red-200">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryCard({ 
  category, 
  gaps,
  highValue
}: { 
  category: string; 
  gaps: NormalizedContentGap[];
  highValue?: NormalizedHighValueAnalysis;
}) {
  const [expanded, setExpanded] = useState(true);
  
  // Determine overall priority for the category (highest priority among gaps)
  const hasHigh = gaps.some(g => g.priority === 'high');
  const hasMedium = gaps.some(g => g.priority === 'medium');
  const overallPriority = hasHigh ? 'high' : hasMedium ? 'medium' : 'low';

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">📋</span>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-white">{category}</h3>
            <span className="text-white/50 text-sm">
              {gaps.length} content idea{gaps.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <PriorityBadge priority={overallPriority} />
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-white/50" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/50" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {/* Preface for content ideas */}
          <p className="text-white/70 text-sm mb-3">
            Content ideas to consider adding to strengthen your authority:
          </p>
          
          {/* Bullet list of content ideas */}
          <ul className="space-y-2">
            {gaps.map((gap, idx) => (
              <li key={idx} className="flex items-start gap-2 text-white/80">
                <Lightbulb className="w-4 h-4 text-[var(--vizzex-yellow)] flex-shrink-0 mt-0.5" />
                <span>{gap.topic}</span>
              </li>
            ))}
          </ul>
          
          {/* Top Posts (from high-value analysis) */}
          {highValue && highValue.topPosts.length > 0 && (
            <TopPostsList posts={highValue.topPosts} />
          )}
          
          {/* Content Guidance (PRO feature) */}
          {highValue?.contentGuidance && (
            <ContentGuidanceSection guidance={highValue.contentGuidance} />
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export default function ContentGapsPage() {
  const { currentSite, isLoading: siteLoading } = useSite();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<NormalizedData | null>(null);

  // Fetch and normalize data
  useEffect(() => {
    const fetchData = async () => {
      const siteId = currentSite?.id;
      if (!siteId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log('[ContentGaps] Fetching data for site:', siteId);
        const response = await analysisApi.getContentGapsData(siteId);
        console.log('[ContentGaps] Raw response keys:', Object.keys(response || {}));
        
        // Normalize IMMEDIATELY - before setting React state
        const normalized = normalizeApiResponse(response);
        console.log('[ContentGaps] Normalized:', {
          hasResults: normalized.hasResults,
          totalGaps: normalized.totalGaps,
          categories: [...normalized.gapsByCategory.keys()],
          highValueCategories: [...normalized.highValueByCategory.keys()],
        });
        
        setData(normalized);
      } catch (err) {
        console.error('[ContentGaps] Error:', err);
        setError('Failed to load content gaps data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentSite?.id]);

  // Loading state
  if (siteLoading || loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[var(--vizzex-yellow)] animate-spin mb-4" />
        <p className="text-white/70">Loading content gaps...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertCircle className="w-10 h-10 text-red-400 mb-4" />
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-[var(--vizzex-yellow)] text-black font-medium rounded hover:opacity-90"
        >
          Retry
        </button>
      </div>
    );
  }

  // No site selected
  if (!currentSite?.id) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Target className="w-10 h-10 text-white/50 mb-4" />
        <p className="text-white/70 mb-2">No site selected</p>
        <Link to="/settings" className="text-[var(--vizzex-yellow)] hover:underline">
          Configure a site in Settings
        </Link>
      </div>
    );
  }

  // No analysis available
  if (!data?.hasResults) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Target className="w-10 h-10 text-white/50 mb-4" />
        <p className="text-white/70 mb-2">No analysis results available</p>
        <p className="text-white/50 text-sm mb-4">Run a baseline analysis first to identify content gaps</p>
        <Link 
          to="/dashboard" 
          className="px-4 py-2 bg-[var(--vizzex-yellow)] text-black font-medium rounded hover:opacity-90"
        >
          Go to Dashboard
        </Link>
      </div>
    );
  }

  // No content gaps (good news!)
  if (data.totalGaps === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Target className="w-7 h-7 text-[var(--vizzex-yellow)]" />
          <h1 className="text-2xl font-bold text-white">Content Gaps</h1>
        </div>

        <div className="flex flex-col items-center justify-center py-16 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)]">
          <CheckCircle2 className="w-12 h-12 text-emerald-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Significant Gaps Identified</h2>
          <p className="text-white/70 text-center max-w-md">
            Great job! Your content covers the key topics in your niche.
          </p>
        </div>
      </div>
    );
  }

  // Sort categories by highest priority
  const sortedCategories = [...data.gapsByCategory.entries()].sort((a, b) => {
    const getPriorityScore = (gaps: NormalizedContentGap[]) => {
      if (gaps.some(g => g.priority === 'high')) return 3;
      if (gaps.some(g => g.priority === 'medium')) return 2;
      return 1;
    };
    return getPriorityScore(b[1]) - getPriorityScore(a[1]);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target className="w-7 h-7 text-[var(--vizzex-yellow)]" />
          <h1 className="text-2xl font-bold text-white">Content Gaps</h1>
        </div>
        {data.analysisDate && (
          <div className="text-white/50 text-sm">
            Last analyzed: {new Date(data.analysisDate).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Category Cards */}
      <div className="space-y-4">
        {sortedCategories.map(([category, gaps]) => (
          <CategoryCard 
            key={category} 
            category={category} 
            gaps={gaps} 
            highValue={data.highValueByCategory.get(category)}
          />
        ))}
      </div>
    </div>
  );
}
